import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/ui/status-badge';
import { FadeInView } from '@/components/ui/fade-in-view';
import { ThemedText } from '@/components/themed-text';
import { formatCurrency } from '@/utils/format-currency';
import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { Charge } from '@/types/charge';
import { CHARGE_STATUS } from '@/types/charge-status';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useSolidChip } from '@/hooks/use-theme-colors';

type TransactionListProps = {
  charges?: Charge[];
  isLoading?: boolean;
  limit?: number;
};

const statusVisuals: Partial<Record<CHARGE_STATUS, { icon: string; color: string; bg: string }>> = {
  [CHARGE_STATUS.PAID]: { icon: 'arrow-down-circle', color: '#16A34A', bg: 'bg-success-50' },
  [CHARGE_STATUS.PENDING]: { icon: 'time-outline', color: '#D97706', bg: 'bg-warning-50' },
  [CHARGE_STATUS.OVERDUE]: { icon: 'alert-circle', color: '#DC2626', bg: 'bg-error-50' },
  [CHARGE_STATUS.REFUNDED]: { icon: 'arrow-back-circle', color: '#737373', bg: 'bg-neutral-100' },
  [CHARGE_STATUS.IN_DISPUTE]: { icon: 'warning', color: '#D97706', bg: 'bg-warning-50' },
};

const fallbackVisual = { icon: 'help-circle', color: '#737373', bg: 'bg-neutral-100' };

export function TransactionList({ charges = [], isLoading = false }: TransactionListProps) {
  const solid = useSolidChip();
  const [page, setPage] = React.useState(1);
  const itemsPerPage = 10;

  // Reset page when charges change
  React.useEffect(() => {
    setPage(1);
  }, [charges]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Transações recentes</CardTitle>
        </CardHeader>
        <CardContent>
          <View className="items-center justify-center py-8">
            <ActivityIndicator size="large" color="#FD5001" />
          </View>
        </CardContent>
      </Card>
    );
  }

  const totalPages = Math.ceil(charges.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const displayCharges = charges.slice(startIndex, startIndex + itemsPerPage);

  if (charges.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Transações recentes</CardTitle>
        </CardHeader>
        <CardContent>
          <View className="items-center justify-center gap-3 py-8">
            <View className="h-14 w-14 items-center justify-center rounded-full bg-neutral-100">
              <Ionicons className="text-neutral-400" name="receipt-outline" size={24} />
            </View>
            <ThemedText className="text-sm text-neutral-500">
              Nenhuma transação encontrada
            </ThemedText>
          </View>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Transações recentes</CardTitle>
      </CardHeader>
      <CardContent className="w-full">
        <View className="w-full">
          {displayCharges.map((charge, index) => {
            const visual = statusVisuals[charge.status] ?? fallbackVisual;
            const date = charge.paidAt ?? charge.dueDate;
            return (
              <FadeInView key={charge.id} delay={index * 40}>
                <View
                  className={`flex-row items-center gap-3 py-3 ${
                    index < displayCharges.length - 1 ? 'border-b border-neutral-100' : ''
                  }`}
                >
                  <View
                    className={`h-10 w-10 items-center justify-center rounded-full ${solid.cls(visual.bg)}`}
                    style={solid.style(visual.color)}
                  >
                    <Ionicons name={visual.icon as any} size={18} color={solid.icon(visual.color)} />
                  </View>
                  <View className="flex-1 gap-0.5">
                    <ThemedText
                      className="font-semibold text-sm text-foreground"
                      style={{ fontVariant: ['tabular-nums'] }}
                    >
                      {formatCurrency(charge.amount)}
                    </ThemedText>
                    <ThemedText className="text-xs text-neutral-500">
                      {format(new Date(date), 'dd/MM/yyyy', { locale: ptBR })}
                    </ThemedText>
                  </View>
                  <StatusBadge status={{ type: 'charge', value: charge.status }} size="sm" />
                </View>
              </FadeInView>
            );
          })}
        </View>

        {totalPages > 1 && (
          <View className="mt-3 flex-row items-center justify-between border-t border-neutral-100 pt-3">
            <ThemedText className="text-sm text-neutral-500">
              Página {page} de {totalPages}
            </ThemedText>
            <View className="flex-row gap-2">
              <Pressable
                onPress={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                accessibilityRole="button"
                accessibilityLabel="Página anterior"
                className={`h-9 w-9 items-center justify-center rounded-full bg-neutral-100 active:opacity-70 ${
                  page === 1 ? 'opacity-40' : ''
                }`}
              >
                <Ionicons className="text-neutral-700" name="chevron-back" size={18} />
              </Pressable>
              <Pressable
                onPress={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                accessibilityRole="button"
                accessibilityLabel="Próxima página"
                className={`h-9 w-9 items-center justify-center rounded-full bg-neutral-100 active:opacity-70 ${
                  page === totalPages ? 'opacity-40' : ''
                }`}
              >
                <Ionicons className="text-neutral-700" name="chevron-forward" size={18} />
              </Pressable>
            </View>
          </View>
        )}
      </CardContent>
    </Card>
  );
}
